import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Alert from '../components/Alert';
import PatientInfoHeader from '../components/PatientInfoHeader';

const StockingSelection = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, hasFeatureAccess } = useAuth();
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    const [productConfig, setProductConfig] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [selections, setSelections] = useState(location.state?.selections || {});
    const [measurements, setMeasurements] = useState(location.state?.measurements || null);
    const [compressionClass] = useState(location.state?.compressionClass || '');
    const [lengthType] = useState(location.state?.lengthType || '');
    const [knitting] = useState(location.state?.knitting || '');
    const [expandedDeviations, setExpandedDeviations] = useState({});
    const [expandedVendorSections, setExpandedVendorSections] = useState({});
    const [isReadOnly] = useState(location.state?.isReadOnly || false);
    const [isApiUnavailable, setIsApiUnavailable] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [productNotes, setProductNotes] = useState(
        location.state?.orderNote || location.state?.currentMeasurement?.order?.orderNote || ''
    );
    const [internalNotes, setInternalNotes] = useState(
        location.state?.notes || location.state?.currentMeasurement?.notes || ''
    );
    const initialSelectedProduct = location.state?.selectedProduct || location.state?.currentMeasurement?.selectedProduct || null;
    const initialSelectedVendorTableId = location.state?.selections?.vendorTableId
        || initialSelectedProduct?.vendorTableID
        || initialSelectedProduct?.vendorTableId
        || '';
    const [currentMeasurement, setCurrentMeasurement] = useState(location.state?.currentMeasurement || null);
    const [isOrdered, setIsOrdered] = useState(location.state?.ordered || location.state?.currentMeasurement?.ordered || false);
    const [matchingProducts, setMatchingProducts] = useState(
        initialSelectedProduct?.productType === 'madeToMeasure' && initialSelectedVendorTableId
            ? []
            : (initialSelectedProduct?.productType === 'madeToMeasure' ? [initialSelectedProduct] : [])
    );
    const [hideMatchingProductsBox, setHideMatchingProductsBox] = useState(false);
    const selectedProduct = currentMeasurement?.selectedProduct || initialSelectedProduct;
    const selectedProductVendorTableId = selectedProduct?.vendorTableID || selectedProduct?.vendorTableId || '';
    const isMadeToMeasureSelectedProduct = selectedProduct?.productType === 'madeToMeasure';
    const isMadeToMeasureMeasurement = currentMeasurement?.measurementType === 'madeToMeasure'
        || location.state?.measurementType === 'madeToMeasure'
        || isMadeToMeasureSelectedProduct;
    const selectedProductVendorName = selectedProduct?.vendorName || '-';
    const selectedProductModelName = selectedProduct?.modelName || '-';
    const isProSubscription = user?.subscription === 'PRO';
    const canShowMadeToMeasureFallback = isProSubscription;
    const isBasicSubscription = user?.subscription === 'BASIC';
    const hasSearchMatchingProducts = (location.state?.matchingProducts?.length || 0) > 0;
    const canRenderOfflineSelection = Boolean(
        (isReadOnly || isOrdered || location.state?.ordered || location.state?.currentMeasurement?.ordered)
        && (currentMeasurement || initialSelectedProduct)
    );

    // Helper function to format patient name as "Lastname, Firstname"
    const formatPatientName = (name) => {
        if (!name || name === 'Unknown' || name === 'Patient') return name;
        const parts = name.trim().split(' ');
        if (parts.length === 1) return name; // Single name, return as-is
        const lastname = parts[parts.length - 1]; // Last word is lastname
        const firstname = parts.slice(0, -1).join(' '); // Everything else is firstname
        return `${lastname}, ${firstname}`;
    };

    // Ref for scrolling to workflow section
    const workflowSectionRef = useRef(null);

    useEffect(() => {
        const metaTags = [
            ['Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0'],
            ['Pragma', 'no-cache'],
            ['Expires', '0'],
        ];

        const managedMetaTags = metaTags.map(([httpEquiv, content]) => {
            let meta = document.head.querySelector(`meta[http-equiv="${httpEquiv}"]`);
            const isCreated = !meta;

            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('http-equiv', httpEquiv);
                document.head.appendChild(meta);
            }

            meta.setAttribute('content', content);
            return { meta, isCreated };
        });

        const syncOfflineState = () => {
            setIsOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
        };

        syncOfflineState();
        window.addEventListener('online', syncOfflineState);
        window.addEventListener('offline', syncOfflineState);
        window.addEventListener('pageshow', syncOfflineState);

        return () => {
            window.removeEventListener('online', syncOfflineState);
            window.removeEventListener('offline', syncOfflineState);
            window.removeEventListener('pageshow', syncOfflineState);

            managedMetaTags.forEach(({ meta, isCreated }) => {
                if (isCreated && meta.parentNode) {
                    meta.parentNode.removeChild(meta);
                }
            });
        };
    }, []);

    // Selection workflow steps loaded from backend config (availableOptions)
    const workflowSteps = useMemo(() => {
        const configuredSteps = productConfig?.availableOptions;

        if (!Array.isArray(configuredSteps)) {
            return [];
        }

        return [...configuredSteps]
            .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
            .map((step) => ({
                id: step.id,
                label: step.optionName,
                dataKey: step.dataKey,
                dependent: step.dependent,
            }));
    }, [productConfig]);

    useEffect(() => {
        fetchProductConfig();
        loadExistingMeasurement();
    }, []);

    useEffect(() => {
        // Check feature access
        if (selections.productType === 'made-to-measure' && !hasFeatureAccess('made-to-measure')) {
            setError(t('measurements.stockingSelection.proRequired'));
            setSelections({ ...selections, productType: undefined });
        }
    }, [selections.productType, hasFeatureAccess, t]);

    const isNetworkFailure = (error) => {
        return !error?.response || error?.message === 'Network Error';
    };

    const shouldRestoreVendorTableWorkflow = (measurement) => {
        return measurement?.selectedProduct?.productType === 'madeToMeasure'
            && !!measurement?.selections?.vendorTableId;
    };



    const loadExistingMeasurement = async () => {
        try {
            const measurementId = location.state?.measurementId;
            if (!measurementId) return;

            const response = await axios.get(`/api/measurements/${measurementId}`);
            const measurement = response.data;

            // Store full measurement for order details display
            setCurrentMeasurement(measurement);

            // Load only order note (do not use patient-level notes)
            setProductNotes(measurement.order?.orderNote || '');
            setInternalNotes(measurement.notes || '');

            // Load ordered status for workflow timeline
            if (measurement.ordered !== undefined) {
                setIsOrdered(measurement.ordered);
            }

            // Load existing selections if available
            if (measurement.selections) {
                setSelections(measurement.selections);
            }

            // Load existing selected product if available
            if (measurement.selectedProduct) {
                if (shouldRestoreVendorTableWorkflow(measurement)) {
                    setMatchingProducts([]);
                } else {
                    setMatchingProducts([measurement.selectedProduct]);
                }
                // Set currentStep to the last step once workflow config is available
                setCurrentStep(workflowSteps.length > 0 ? workflowSteps.length - 1 : Number.MAX_SAFE_INTEGER);
            }

            setIsApiUnavailable(false);
        } catch (error) {
            console.error('Error loading existing measurement:', error);
            if (isNetworkFailure(error) && !canRenderOfflineSelection) {
                setIsApiUnavailable(true);
            }
        }
    };

    const fetchProductConfig = async () => {
        try {
            const response = await axios.get('/api/products/config');
            setProductConfig(response.data);
            setIsApiUnavailable(false);
        } catch (error) {
            console.error('Error fetching product config:', error);
            if (isNetworkFailure(error) && !canRenderOfflineSelection) {
                setIsApiUnavailable(true);
            }
        }
    };

    useEffect(() => {
        if (!selectedProduct) return;

        setSelections((prev) => {
            const next = { ...prev };
            let changed = false;

            if (!next.vendor && selectedProduct.vendorName) {
                next.vendor = selectedProduct.vendorName;
                changed = true;
            }

            if (!next.vendortype && selectedProduct.vendorType) {
                next.vendortype = selectedProduct.vendorType;
                changed = true;
            }

            if (!next.vendorTableId && selectedProductVendorTableId) {
                next.vendorTableId = selectedProductVendorTableId;
                changed = true;
            }

            const fallbackModelId = selectedProduct.modelId || selectedProduct.vendorTableID || selectedProduct.vendorTableId;
            if (!next.model && fallbackModelId) {
                next.model = fallbackModelId;
                changed = true;
            }

            return changed ? next : prev;
        });
    }, [selectedProduct, selectedProductVendorTableId]);

    useEffect(() => {
        if (selectedProduct?.productType !== 'madeToMeasure') return;
        if (!selections.vendorTableId && !selectedProductVendorTableId) return;
        // Keep summary visible once user reached the selected-product state.
        if (hideMatchingProductsBox) return;

        setMatchingProducts([]);
        setHideMatchingProductsBox(false);
    }, [selectedProduct, selections.vendorTableId, selectedProductVendorTableId, hideMatchingProductsBox]);

    const resolveVendorAndModelFromSelection = () => {
        if (!productConfig) {
            return { vendor: null, model: null };
        }

        const activeVendorName = selections.vendor || selectedProduct?.vendorName || '';
        const activeVendorTableId = selections.vendorTableId || selectedProductVendorTableId || '';

        let vendor = null;
        if (activeVendorName) {
            vendor = productConfig.vendorTables.find((v) => v.id === activeVendorName || v.name === activeVendorName) || null;
        }

        if (!vendor && activeVendorTableId) {
            vendor = productConfig.vendorTables.find((v) =>
                Array.isArray(v.models) && v.models.some((m) => m.vendorTableId === activeVendorTableId || m.id === activeVendorTableId)
            ) || null;
        }

        let model = null;
        if (vendor) {
            if (selections.model) {
                model = vendor.models.find((m) => m.id === selections.model || m.name === selections.model || m.vendorTableId === selections.model) || null;
            }

            if (!model && activeVendorTableId) {
                model = vendor.models.find((m) => m.vendorTableId === activeVendorTableId || m.id === activeVendorTableId) || null;
            }
        }

        return { vendor, model };
    };

    // Extract deviation details from a product
    const getDeviationDetails = (product) => {
        if (!product || product.totalDev === 0) return [];

        const deviations = [];
        const fieldCategories = ['widths', 'lengths', 'foots'];

        fieldCategories.forEach(category => {
            const categoryData = product[category];
            if (!categoryData || !Array.isArray(categoryData)) return;

            categoryData.forEach(item => {
                Object.keys(item).forEach(key => {
                    // Check if this field has a _dev suffix
                    if (key.endsWith('_dev')) {
                        const devValue = item[key];
                        if (devValue !== undefined && devValue !== null && devValue !== 0) {
                            const baseFieldName = key.replace('_dev', '');
                            const minFieldName = `${baseFieldName}_min`;
                            const maxFieldName = `${baseFieldName}_max`;

                            // Get min and max values
                            const minValue = item[minFieldName];
                            const maxValue = item[maxFieldName];

                            // Get measured value from measurements object
                            let measuredValue = null;
                            if (measurements && measurements[baseFieldName] !== undefined) {
                                measuredValue = measurements[baseFieldName];
                            }
                            if (measurements && measurements[baseFieldName + 'L'] !== undefined) {
                                measuredValue = measurements[baseFieldName + 'L'];
                            }
                            if (measurements && measurements[baseFieldName + 'R'] !== undefined) {
                                measuredValue = measurements[baseFieldName + 'R'];
                            }

                            // Format field name for display
                            const displayName = baseFieldName
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, l => l);

                            deviations.push({
                                leg: item.LL ? 'Left leg' : item.LR ? 'Right leg' : '',
                                fieldName: displayName,
                                rawFieldName: baseFieldName,
                                category: category,
                                minValue: minValue,
                                maxValue: maxValue,
                                deviation: devValue,
                                measuredValue: measuredValue
                            });
                        }
                    }
                });
            });
        });

        return deviations;
    };

    const toggleDeviationExpansion = (productIndex) => {
        setExpandedDeviations(prev => ({
            ...prev,
            [productIndex]: !prev[productIndex]
        }));
    };

    const toggleVendorSection = (sectionKey) => {
        setExpandedVendorSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Calculate the correct total deviation accounting for both legs
    const calculateTotalDeviation = (product) => {
        if (!product) return 0;

        let totalDev = 0;

        // Sum deviations from widths (both left and right legs)
        if (product.widths && Array.isArray(product.widths)) {
            product.widths.forEach(width => {
                if (width.totalDevWidthsR) totalDev += Math.abs(width.totalDevWidthsR);
                if (width.totalDevWidthsL) totalDev += Math.abs(width.totalDevWidthsL);
            });
        }

        /*
        // Sum deviations from lengths
        if (product.lengths && Array.isArray(product.lengths)) {
            product.lengths.forEach(length => {
                if (length.totalDevLengths) totalDev += Math.abs(length.totalDevLengths);
            });
        }

        // Sum deviations from foots
        if (product.foots && Array.isArray(product.foots)) {
            product.foots.forEach(foot => {
                if (foot.totalDevFoots) totalDev += Math.abs(foot.totalDevFoots);
            });
        }
        */

        return totalDev;
    };

    // Calculate deviation for a specific size (widthGroup)
    const calculateSizeDeviation = (widthGroup) => {
        if (!widthGroup) return 0;

        let sizeDev = 0;

        // Sum left leg width deviations
        if (widthGroup.legs.left && widthGroup.legs.left.totalDevWidthsL) {
            sizeDev += Math.abs(widthGroup.legs.left.totalDevWidthsL);
        }

        // Sum right leg width deviations
        if (widthGroup.legs.right && widthGroup.legs.right.totalDevWidthsR) {
            sizeDev += Math.abs(widthGroup.legs.right.totalDevWidthsR);
        }

        /*
        // Sum length deviations for this size
        if (widthGroup.lengths && Array.isArray(widthGroup.lengths)) {
            sizeDev += Math.abs(widthGroup.lengths[0].totalDevLengths);
        }
        */

        return sizeDev;
    };

    // Group matching products by Vendor → Width Size → Left/Right Leg
    const groupMatchingProducts = (products) => {
        if (!products || products.length === 0) return [];

        const grouped = [];

        products.forEach(product => {
            const { vendorName, vendorType, widths = [], lengths = [] } = product;

            // Find or create vendor group
            let vendorGroup = grouped.find(g => g.vendorName === vendorName && g.vendorType === vendorType);
            if (!vendorGroup) {
                vendorGroup = {
                    vendorName,
                    vendorType,
                    product, // Keep reference to original product for selection
                    widthGroups: []
                };
                grouped.push(vendorGroup);
            }

            // Group widths by vendorModel
            const widthsByModel = {};
            widths.forEach(width => {
                const modelName = width.vendorModel || 'Unknown Size';
                if (!widthsByModel[modelName]) {
                    widthsByModel[modelName] = { right: null, left: null };
                }
                if (width.LR) {
                    widthsByModel[modelName].right = width;
                }
                if (width.LL) {
                    widthsByModel[modelName].left = width;
                }
            });

            // Add width groups
            Object.entries(widthsByModel).forEach(([modelName, legs]) => {
                const existingWidthGroup = vendorGroup.widthGroups.find(wg => wg.modelName === modelName);
                if (!existingWidthGroup) {
                    vendorGroup.widthGroups.push({
                        modelName,
                        legs,
                        lengths,
                        product
                    });
                }
            });
        });

        // Sort vendors by total deviation (lowest first) then alphabetically
        grouped.sort((a, b) => {
            const devA = calculateTotalDeviation(a.product);
            const devB = calculateTotalDeviation(b.product);
            if (devA !== devB) return devA - devB;
            return (a.vendorName || '').localeCompare(b.vendorName || '');
        });

        // Sort width groups alphabetically within each vendor
        grouped.forEach(vendor => {
            vendor.widthGroups.sort((a, b) =>
                (a.modelName || '').localeCompare(b.modelName || '')
            );
        });

        return grouped;
    };

    // Get deviation details for a specific width/length object
    const getWidthDeviationDetails = (widthObject, legSide = null) => {
        if (!widthObject) return [];

        const deviations = [];

        // Determine which leg this is for based on the widthObject or legSide parameter
        const isLeftLeg = legSide === 'left' || (widthObject.LL === true);
        const isRightLeg = legSide === 'right' || (widthObject.LR === true);

        Object.keys(widthObject).forEach(key => {
            if (key.endsWith('_dev')) {
                const devValue = widthObject[key];
                if (devValue !== undefined && devValue !== null && devValue !== 0) {
                    const baseFieldName = key.replace('_dev', '');
                    const minFieldName = `${baseFieldName}_min`;
                    const maxFieldName = `${baseFieldName}_max`;

                    const minValue = widthObject[minFieldName];
                    const maxValue = widthObject[maxFieldName];

                    // Look up the correct measurement based on which leg this is for
                    let measuredValue = null;
                    if (measurements) {
                        if (isLeftLeg) {
                            // For left leg, try L suffix first, then base field
                            measuredValue = measurements[baseFieldName + 'L'] ?? measurements[baseFieldName];
                        } else if (isRightLeg) {
                            // For right leg, try R suffix first, then base field
                            measuredValue = measurements[baseFieldName + 'R'] ?? measurements[baseFieldName];
                        } else {
                            // Fallback: check all variants if leg side cannot be determined
                            measuredValue = measurements[baseFieldName] ?? measurements[baseFieldName + 'L'] ?? measurements[baseFieldName + 'R'];
                        }
                    }

                    // Validate: Only show deviation if measured value is outside the range
                    // This filters out incorrect deviations from backend data
                    if (measuredValue !== null && minValue !== undefined && maxValue !== undefined) {
                        const actualDeviation = measuredValue < minValue ? (minValue - measuredValue) :
                            measuredValue > maxValue ? (measuredValue - maxValue) : 0;

                        // Skip this deviation if the measured value is actually within range
                        if (actualDeviation === 0) {
                            return;
                        }
                    }

                    const displayName = baseFieldName
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l);

                    deviations.push({
                        fieldName: displayName,
                        rawFieldName: baseFieldName,
                        minValue: minValue,
                        maxValue: maxValue,
                        deviation: devValue,
                        measuredValue: measuredValue
                    });
                }
            }
        });

        return deviations;
    };

    // Helper function to evaluate option filters
    const evaluateOptionFilter = (filter, vendorTableId) => {
        if (!filter) return true;

        try {
            const selectedVendorTableId = selections.vendorTableId || '';
            const currentLengthType = measurements?.lengthType || lengthType;

            console.log('Evaluating filter:', filter);
            console.log('Current lengthType:', currentLengthType);
            console.log('compressionClass:', compressionClass);
            console.log('knitting:', knitting);
            console.log('selections object:', selections);

            if (!currentLengthType) {
                console.warn('No lengthType available');
                return true;
            }

            // Normalize all substituted values and string literals to lowercase+trimmed for case-insensitive matching
            // All spaces are stripped so e.g. 'Tricot Band' == 'TricotBand' evaluates as equal
            const norm = (v) => (v || '').replace(/\s+/g, '').toLowerCase();
            let expression = filter.replace(/[{}]/g, '').trim()
                .replace(/lengthType/gi, `'${norm(currentLengthType)}'`)
                .replace(/compressionClass/gi, `'${norm(compressionClass)}'`)
                .replace(/knitting/gi, `'${norm(knitting)}'`)
                .replace(/lengthsize/gi, `'${norm(selections.lengthsize)}'`)
                .replace(/size/gi, `'${norm(selections.size)}'`)
                .replace(/bandType/gi, `'${norm(selections.bandType)}'`)
                .replace(/toeOption/gi, `'${norm(selections.toeOption)}'`)
                .replace(/footLength/gi, `'${norm(selections.footLength)}'`)
                .replace(/bandWidth/gi, `'${norm(selections.bandWidth)}'`)
                .replace(/ankle/gi, `'${norm(selections.ankle)}'`)
                .replace(/color/gi, `'${norm(selections.color)}'`)
                .replace(/design/gi, `'${norm(selections.design)}'`)
                .replace(/fabric/gi, `'${norm(selections.fabric)}'`)
                .replace(/leftright/gi, `'${norm(selections.leftright)}'`);
            expression = expression.replace(/'([^']*)'/g, (_, s) => `'${norm(s)}'`);
            expression += ` && '${norm(selectedVendorTableId)}' === '${norm(vendorTableId)}'`;
            console.log('Option filter expression to evaluate:', expression);

            // eslint-disable-next-line no-eval
            const result = eval(expression);
            console.log('Filter result:', result);
            return result;
        } catch (error) {
            console.warn('Error evaluating Option filter:', filter, error);
            return false;
        }
    };

    // Helper function to evaluate model filters
    const evaluateModelFilter = (filter, vendorTableId) => {
        if (!filter) return true;

        try {
            const selectedVendorTableId = selections.vendorTableId || '';
            const currentLengthType = measurements?.lengthType || lengthType;

            console.log('Evaluating model filter:', filter);
            console.log('Current lengthType:', currentLengthType);
            console.log('compressionClass:', compressionClass);
            console.log('knitting:', knitting);
            console.log('measurements object:', measurements);

            if (!currentLengthType) {
                console.warn('No lengthType available');
                return true;
            }

            // Normalize all substituted values and string literals to lowercase+trimmed for case-insensitive matching
            let expression = filter.replace(/[{}]/g, '').trim()
                .replace(/lengthType/g, `'${currentLengthType.trim().toLowerCase()}'`)
                .replace(/compressionClass/g, `'${compressionClass.trim().toLowerCase()}'`)
                .replace(/knitting/g, `'${knitting.trim().toLowerCase()}'`);
            expression = expression.replace(/'([^']*)'/g, (_, s) => `'${s.trim().toLowerCase()}'`);
            expression += ` && '${selectedVendorTableId.trim().toLowerCase()}' === '${vendorTableId.trim().toLowerCase()}'`;
            console.log('Model filter expression to evaluate:', expression);

            // eslint-disable-next-line no-eval
            const result = eval(expression);
            console.log('Model filter result:', result);
            return result;
        } catch (error) {
            console.warn('Error evaluating model filter:', filter, error);
            return false;
        }
    };

    const getAvailableOptions = (stepId) => {
        if (!productConfig) return [];

        const step = workflowSteps.find((s) => s.id === stepId);
        const { vendor: resolvedVendor, model: resolvedModel } = resolveVendorAndModelFromSelection();
        const activeVendorTableId = selections.vendorTableId || selectedProductVendorTableId;

        if (step.options) {
            return step.options;
        }

        if (step.dataKey === 'vendors') {
            // Get matchingProducts from location.state
            const matchingProductsData = location.state?.matchingProducts || [];

            // Extract unique vendorNames from matchingProducts
            const matchingVendorNames = matchingProductsData.length > 0
                ? [...new Set(matchingProductsData.map(p => p.vendorName).filter(Boolean))]
                : [];


            // Filter vendors to only show those that appear in matchingProducts
            let vendors = [];
            if (matchingVendorNames.length > 0) {
                vendors = productConfig.vendorTables.filter(v =>
                    matchingVendorNames.includes(v.name)
                );
            }

            return vendors.map((v) => ({
                id: v.id,
                label: v.name,
                value: v.id,
            }));
        }

        if (stepId === 'vendortype') {
            const vendor = productConfig.vendorTables.find((v) => v.id === selections.vendor);
            if (!vendor) return [];

            // Get matchingProducts from location.state
            const matchingProductsData = location.state?.matchingProducts || [];

            // Extract matchingProducts for the selected vendor
            const matchingVendorProductsData = matchingProductsData.filter(p => p.vendorName === selections.vendor);
            console.log(`Matching products from ${selections.vendor}:`, matchingVendorProductsData);

            return matchingVendorProductsData.map((p) => (
                {
                    id: p.vendorType,
                    label: p.vendorType,
                    value: p.vendorType,
                }));
        }

        if (step.dataKey === 'sizes') {
            // Get matchingProducts from location.state
            const matchingProductsData = location.state?.matchingProducts || [];

            // Extract matchingProducts for the selected vendor
            const matchingVendorProductsData = matchingProductsData.filter(p => p.vendorName === selections.vendor);
            console.log(`Matching products from ${selections.vendor}:`, matchingVendorProductsData);

            let matchingVendorProductWidths = [];
            let uniqueWidthsSet = new Set();
            for (let index = 0; index < matchingVendorProductsData.length; index++) {
                const matchingVendorProduct = matchingVendorProductsData[index];
                for (let index = 0; index < matchingVendorProduct.widths.length; index++) {
                    const matchingVendorProductWidth = matchingVendorProduct.widths[index];
                    if (!uniqueWidthsSet.has(matchingVendorProductWidth.vendorModel)) {
                        uniqueWidthsSet.add(matchingVendorProductWidth.vendorModel);
                        matchingVendorProductWidths.push({
                            id: matchingVendorProductWidth.vendorModel,
                            label: matchingVendorProductWidth.vendorModel,
                            value: matchingVendorProductWidth.vendorModel,
                        });
                    }
                }
            }

            console.log(`Matching product widths from ${selections.vendor}:`, matchingVendorProductWidths);
            return matchingVendorProductWidths;

        }

        /*         if (step.dataKey === 'lengthsizes') {
                    const vendor = productConfig.vendorTables.find((v) => v.id === selections.vendor);
                    if (!vendor) return [];
        
                    // Get matchingProducts from location.state
                    const matchingProductsData = location.state?.matchingProducts || [];
        
                    // Extract matchingProducts for the selected vendor
                    const matchingVendorProductsData = matchingProductsData.filter(p => p.vendorName === selections.vendor);
                    console.log(`Matching products from ${selections.vendor}:`, matchingVendorProductsData);
        
                    let matchingVendorProductLengths = [];
                    let uniqueLengthsSet = new Set();
                    for (let index = 0; index < matchingVendorProductsData.length; index++) {
                        const matchingVendorProduct = matchingVendorProductsData[index];
                        for (let index = 0; index < matchingVendorProduct.lengths.filter(l => l.type == measurements.lengthType).length; index++) {
                            const matchingVendorProductLength = matchingVendorProduct.lengths[index];
                            if (!uniqueLengthsSet.has(matchingVendorProductLength.vendorModel)) {
                                uniqueLengthsSet.add(matchingVendorProductLength.vendorModel);
                                matchingVendorProductLengths.push({
                                    id: matchingVendorProductLength.vendorModel,
                                    label: matchingVendorProductLength.vendorModel,
                                    value: matchingVendorProductLength.vendorModel,
                                });
                            }
                        }
                    }
                    console.log(`Matching product lengths from ${selections.vendor}:`, matchingVendorProductLengths);
                    return matchingVendorProductLengths;
                }
         */
        if (stepId === 'model' && selections.vendor) {
            const vendor = resolvedVendor || productConfig.vendorTables.find((v) => v.id === selections.vendor || v.name === selections.vendor);
            if (!vendor) return [];

            // Filter models based on modelFilter if present
            const filteredModels = vendor.models.filter((m) => {
                if (activeVendorTableId && m.vendorTableId !== activeVendorTableId && m.id !== activeVendorTableId) {
                    return false;
                }

                if (m.modelFilter) {
                    return evaluateModelFilter(m.modelFilter, m.vendorTableId);
                }
                return true;
            });

            // Sort models by displayOrder
            const sortedModels = [...filteredModels].sort((a, b) => {
                const orderA = a.displayOrder ?? 0;
                const orderB = b.displayOrder ?? 0;
                return orderA - orderB;
            });

            return sortedModels.map((m) => ({
                id: m.id,
                label: m.name,
                value: m.id,
            }));
        }

        // Get options from the productConfig (MongoDB-backed)
        if ((selections.vendor && selections.model) || (resolvedVendor && resolvedModel)) {
            const vendor = resolvedVendor || productConfig.vendorTables.find((v) => v.id === selections.vendor || v.name === selections.vendor);
            const model = resolvedModel || vendor?.models.find((m) => m.id === selections.model);

            if (!model || !model.options || model.options.length === 0) return [];

            // Map stepId to the corresponding option ID in product config
            const stepToOptionIdMap = {
                'leftright': 'leftright',
                'toeOption': 'toeOption',
                'footLength': 'footLength',
                'lengthsize': 'lengthsize',
                'bandType': 'bandType',
                'bandWidth': 'bandWidth',
                'ankle': 'ankle',
                'color': 'color',
                'design': 'design',
                'pantyShortCompression': 'pantyShortCompression',
                'pantyShortSize': 'pantyShortSize',
                'pantyCrotch': 'pantyCrotch',
                'fabric': 'fabric'
            };

            const targetOptionId = stepToOptionIdMap[stepId];
            if (!targetOptionId) return [];

            // Find all matching options by ID and filter by optionFilter if present
            const matchingOptions = model.options.filter(option => {
                // Check if option ID matches
                if (option.id !== targetOptionId) return false;

                // If there's a filter, evaluate it
                if (option.optionFilter) {
                    return evaluateOptionFilter(option.optionFilter, model.vendorTableId);
                }

                return true;
            });

            // Sort matching options by displayOrder
            const sortedMatchingOptions = [...matchingOptions].sort((a, b) => {
                const orderA = a.displayOrder ?? 0;
                const orderB = b.displayOrder ?? 0;
                return orderA - orderB;
            });

            // Collect all option values from matching options (maintaining their order)
            const allValues = [];
            sortedMatchingOptions.forEach(option => {
                if (option.optionValues && option.optionValues.length > 0) {
                    // Sort option values by displayOrder
                    const sortedValues = [...option.optionValues].sort((a, b) => {
                        const orderA = a.displayOrder ?? 0;
                        const orderB = b.displayOrder ?? 0;
                        return orderA - orderB;
                    });
                    allValues.push(...sortedValues);
                }
            });

            // Remove duplicates while preserving order
            const uniqueValues = [];
            const seenValues = new Set();
            allValues.forEach(optVal => {
                const val = typeof optVal === 'string' ? optVal : optVal.value;
                if (!seenValues.has(val)) {
                    seenValues.add(val);
                    uniqueValues.push(optVal);
                }
            });

            return uniqueValues.map((opt) => ({
                id: typeof opt === 'string' ? opt : opt.value,
                label: typeof opt === 'string' ? opt : opt.value,
                value: typeof opt === 'string' ? opt : opt.value,
            }));
        }

        return [];
    };

    // Get display label for a selection value
    const getSelectionLabel = (stepId, value) => {
        if (!value) return '';

        // For model, need to look up the model name
        if (stepId === 'model' && productConfig && selections.vendor) {
            const vendor = productConfig.vendorTables.find((v) => v.id === selections.vendor || v.name === selections.vendor);
            const model = vendor?.models.find((m) => m.id === value);
            return model?.name || value;
        }

        // For vendor, look up vendor name
        if (stepId === 'vendor' && productConfig) {
            const vendor = productConfig.vendorTables.find((v) => v.id === value);
            return vendor?.name || value;
        }

        // For options, look up the option name if needed
        if (productConfig && selections.vendor && selections.model) {
            const vendor = productConfig.vendorTables.find((v) => v.id === selections.vendor);
            const model = vendor?.models.find((m) => m.id === selections.model);

            if (model && model.options) {
                const stepToOptionIdMap = {
                    'leftright': 'leftright',
                    'toeOption': 'toeOption',
                    'footLength': 'footLength',
                    'lengthsize': 'lengthsize',
                    'bandType': 'bandType',
                    'bandWidth': 'bandWidth',
                    'ankle': 'ankle',
                    'color': 'color',
                    'design': 'design',
                    'pantyShortCompression': 'pantyShortCompression',
                    'pantyShortSize': 'pantyShortSize',
                    'pantyCrotch': 'pantyCrotch',
                    'fabric': 'fabric',
                };

                const targetOptionId = stepToOptionIdMap[stepId];
                if (targetOptionId) {
                    const option = model.options.find(opt => opt.id === targetOptionId);
                    // For option values, just return the value itself (it's already the display value)
                    return value;
                }
            }
        }

        // Default: return the value as-is
        return value;
    };

    // Filter workflow steps to only include steps with available options
    const visibleWorkflowSteps = useMemo(() => {
        return workflowSteps.filter(step => {
            const options = getAvailableOptions(step.id);
            return options && options.length > 0;
        });
    }, [workflowSteps, productConfig, selections, measurements, lengthType]);

    // Cap currentStep at the last visible step to prevent advancing beyond visible steps
    useEffect(() => {
        if (visibleWorkflowSteps.length > 0 && currentStep >= visibleWorkflowSteps.length) {
            setCurrentStep(visibleWorkflowSteps.length - 1);
        }
    }, [visibleWorkflowSteps.length, currentStep]);

    const handleSelection = (stepId, value) => {
        const newSelections = { ...selections, [stepId]: value };

        // Special handling for vendortype: also store vendorTableId for filtering
        if (stepId === 'vendortype') {
            const matchingProductsData = location.state?.matchingProducts || [];
            const matchingProduct = matchingProductsData.find(
                p => p.vendorName === selections.vendor && p.vendorType === value
            );
            if (matchingProduct && matchingProduct.vendorTableID) {
                newSelections.vendorTableId = matchingProduct.vendorTableID;
            }
        }

        // Clear dependent selections
        const stepIndex = workflowSteps.findIndex((s) => s.id === stepId);
        workflowSteps.slice(stepIndex + 1).forEach((step) => {
            delete newSelections[step.id];
        });

        setSelections(newSelections);

        // Auto-advance to the next step in the workflow
        const currentStepIndexInFull = workflowSteps.findIndex((s) => s.id === stepId);
        if (currentStepIndexInFull < workflowSteps.length - 1) {
            setTimeout(() => setCurrentStep(currentStep + 1), 300);
        }
    };

    // Handle clicking on a matching product from the blue info box
    const handleMatchingProductClick = (product) => {
        if (isReadOnly || isOrdered) return;

        const newSelections = {
            vendor: product.vendorName,
            vendortype: product.vendorType,
            vendorTableId: product.vendorTableID,
        };

        setSelections(newSelections);

        // Collapse all expanded deviations
        setExpandedDeviations({});

        // Auto-advance to the first step in the workflow (Size)
        setCurrentStep(0);

        // Scroll to the workflow section after a brief delay to allow state updates
        setTimeout(() => {
            if (workflowSectionRef.current) {
                workflowSectionRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 100);
    };

    const findMatchingProducts = () => {
        if (!productConfig) return;

        const matches = [];
        const isMadeToMeasureWorkflow = selectedProduct?.productType === 'madeToMeasure'
            || currentMeasurement?.measurementType === 'madeToMeasure'
            || location.state?.measurementType === 'madeToMeasure'
            || !!selections.vendorTableId;

        productConfig.vendorTables.forEach((vendor) => {
            if (selections.vendor && vendor.id !== selections.vendor && vendor.name !== selections.vendor) return;

            vendor.models.forEach((model) => {
                if (
                    selections.model
                    && model.id !== selections.model
                    && model.name !== selections.model
                    && model.vendorTableId !== selections.model
                ) return;

                if (
                    selections.vendorTableId
                    && model.vendorTableId
                    && model.vendorTableId !== selections.vendorTableId
                    && model.id !== selections.vendorTableId
                ) return;

                const resolvedVendorTableId = selections.vendorTableId
                    || model.vendorTableId
                    || selectedProductVendorTableId
                    || '';

                // Build a product object from the selected model and selections
                const productMatch = {
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    vendorType: selections.vendortype || selectedProduct?.vendorType || '',
                    modelId: model.id,
                    modelName: model.name,
                    vendorTableID: resolvedVendorTableId,
                    vendorTableId: resolvedVendorTableId,
                    productType: isMadeToMeasureWorkflow ? 'madeToMeasure' : 'offTheShelf',
                    compressionClass: compressionClass || measurements?.compressionClass || '',
                    lengthType: lengthType,
                    knitting: knitting,
                    // Collect all selected options
                    options: [],
                };

                // Add measurement-based options
                if (lengthType) {
                    productMatch.options.push({ name: 'Length Type', value: lengthType });
                }
                if (knitting) {
                    productMatch.options.push({ name: 'Knitting', value: knitting });
                }

                // Add each selection as an option
                if (selections.size) {
                    productMatch.options.push({ name: 'Size', value: selections.size });
                    productMatch.size = selections.size;
                }
                if (selections.lengthsize) {
                    productMatch.options.push({ name: 'Length Size', value: selections.lengthsize });
                    productMatch.lengthsize = selections.lengthsize;
                }
                if (selections.leftright) {
                    productMatch.options.push({ name: 'Left/Right', value: selections.leftright });
                    productMatch.leftright = selections.leftright;
                }
                if (selections.toeOption) {
                    productMatch.options.push({ name: 'Toe Option', value: selections.toeOption });
                    productMatch.toeOption = selections.toeOption;
                }
                if (selections.bandType) {
                    productMatch.options.push({ name: 'Band Type', value: selections.bandType });
                    productMatch.bandType = selections.bandType;
                }
                if (selections.bandWidth) {
                    productMatch.options.push({ name: 'Band Width', value: selections.bandWidth });
                    productMatch.bandWidth = selections.bandWidth;
                }
                if (selections.footLength) {
                    productMatch.options.push({ name: 'Foot Length', value: selections.footLength });
                    productMatch.footLength = selections.footLength;
                }
                if (selections.ankle) {
                    productMatch.options.push({ name: 'Ankle', value: selections.ankle });
                    productMatch.ankle = selections.ankle;
                }
                if (selections.color) {
                    productMatch.options.push({ name: 'Color', value: selections.color });
                    productMatch.color = selections.color;
                }
                if (selections.design) {
                    productMatch.options.push({ name: 'Design', value: selections.design });
                    productMatch.design = selections.design;
                }
                if (selections.pantyShortCompression) {
                    productMatch.options.push({ name: 'Panty Short Compression', value: selections.pantyShortCompression });
                    productMatch.pantyShortCompression = selections.pantyShortCompression;
                }
                if (selections.pantyShortSize) {
                    productMatch.options.push({ name: 'Panty Short Size', value: selections.pantyShortSize });
                    productMatch.pantyShortSize = selections.pantyShortSize;
                }
                if (selections.pantyCrotch) {
                    productMatch.options.push({ name: 'Panty Crotch', value: selections.pantyCrotch });
                    productMatch.pantyCrotch = selections.pantyCrotch;
                }
                if (selections.fabric) {
                    productMatch.options.push({ name: 'Fabric', value: selections.fabric });
                    productMatch.fabric = selections.fabric;
                }

                matches.push(productMatch);
            });
        });

        if (matches.length === 0 && (selections.model || selections.vendor)) {
            const { vendor: resolvedVendor, model: resolvedModel } = resolveVendorAndModelFromSelection();

            matches.push({
                vendorId: resolvedVendor?.id || '',
                vendorName: resolvedVendor?.name || selections.vendor || '',
                vendorType: selections.vendortype || selectedProduct?.vendorType || '',
                modelId: resolvedModel?.id || selections.model || '',
                modelName: resolvedModel?.name || selections.model || '',
                vendorTableID: selections.vendorTableId || selectedProductVendorTableId || '',
                vendorTableId: selections.vendorTableId || selectedProductVendorTableId || '',
                productType: isMadeToMeasureWorkflow ? 'madeToMeasure' : 'offTheShelf',
                compressionClass: compressionClass || measurements?.compressionClass || '',
                lengthType: lengthType,
                knitting: knitting,
            });
        }

        setMatchingProducts(matches);
    };

    const handleNavigateToMeasurements = () => {
        // Direct navigation to LegMeasuring page (used in read-only mode)
        navigate('/leg-measuring', {
            state: {
                measurementId: location.state?.measurementId,
                patientId: location.state?.patientId,
                patientName: location.state?.patientName,
                measurements: measurements,
                compressionClass: compressionClass,
                lengthType: lengthType,
                knitting: knitting,
                isReadOnly: isReadOnly,
            }
        });
    };

    const handleNavigateToMadeToMeasure = () => {
        navigate('/leg-measuring', {
            state: {
                measurementId: location.state?.measurementId,
                patientId: location.state?.patientId,
                patientName: location.state?.patientName,
                measurements,
                compressionClass,
                lengthType,
                knitting,
                measurementType: 'madeToMeasure',
                isReadOnly,
            }
        });
    };

    const handleNavigateToUpgrade = () => {
        navigate('/organization-settings', {
            state: { tab: 'subscription' }
        });
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            // Navigate back to LegMeasuring page with the current state
            navigate('/leg-measuring', {
                state: {
                    measurementId: location.state?.measurementId,
                    patientId: location.state?.patientId,
                    patientName: location.state?.patientName,
                    measurements: measurements,
                    compressionClass: compressionClass,
                    lengthType: lengthType,
                    knitting: knitting, isReadOnly: isReadOnly,
                }
            });
        }
    };

    const handleNext = () => {
        if (currentStep < visibleWorkflowSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            setHideMatchingProductsBox(true);
            findMatchingProducts();
        }
    };

    const handleProductSelect = async (product) => {
        // Navigate to Order page with product data
        navigate('/order', {
            state: {
                ...location.state,
                selectedProduct: product,
                currentMeasurement,
                measurements,
                compressionClass,
                lengthType,
                knitting,
                selections,
                isReadOnly,
                ordered: false  // Do not set ordered=true when entering order details
            }
        });
    };

    const handleSaveProductOnly = async (product) => {
        try {
            let measurementId = location.state?.measurementId;
            const normalizeType = (value) => String(value || '')
                .trim()
                .toLowerCase()
                .replace(/[\s_-]/g, '');

            const isMadeToMeasureType = (value) => normalizeType(value) === 'madetomeasure';
            const resolvedMeasurementType = isMadeToMeasureType(
                currentMeasurement?.measurementType || location.state?.measurementType || selections.productType
            )
                ? 'madeToMeasure'
                : 'offTheShelf';

            // If no measurementId exists, create a new measurement first
            if (!measurementId) {
                const patientId = location.state?.patientId;
                const patientName = location.state?.patientName;

                if (!patientId || !patientName) {
                    setError('Missing patient information. Please go back and complete the patient info and measurements.');
                    setTimeout(() => navigate('/patient-info'), 2000);
                    return;
                }

                // Get patient details from sessionStorage if available
                const storedPatientInfo = sessionStorage.getItem('patientInfo');
                let patientDetails = {
                    dateOfBirth: '',
                    gender: '',
                    address: '',
                    phoneNumber: '',
                    email: '',
                    notes: '',
                };

                if (storedPatientInfo) {
                    const patientData = JSON.parse(storedPatientInfo);
                    patientDetails = {
                        dateOfBirth: patientData.dateOfBirth || '',
                        gender: patientData.gender || '',
                        address: patientData.address || '',
                        phoneNumber: patientData.phoneNumber || '',
                        email: patientData.email || '',
                        notes: patientData.notes || '',
                    };
                }

                // Create a new measurement
                const measurementData = {
                    userId: user?._id,
                    organizationId: user?.organization,
                    patientId,
                    patientName,
                    patientDateOfBirth: patientDetails.dateOfBirth,
                    patientGender: patientDetails.gender,
                    patientAddress: patientDetails.address,
                    patientPhoneNumber: patientDetails.phoneNumber,
                    patientEmail: patientDetails.email,
                    notes: patientDetails.notes,
                    measurements,
                    compressionClass,
                    lengthType,
                    knitting,
                    measurementType: resolvedMeasurementType,
                    completedSteps: ['patientInfo', 'measurements'],
                };

                const createResponse = await axios.post('/api/measurements', measurementData);
                measurementId = createResponse.data._id;
            }

            const persistedSelections = {
                ...selections,
                vendor: selections.vendor || product.vendorName,
                vendortype: selections.vendortype || product.vendorType,
                vendorTableId: selections.vendorTableId || product.vendorTableID || product.vendorTableId,
                model: selections.model || product.modelId,
            };

            const inferredProductType = resolvedMeasurementType;

            // Prepare the product data for saving
            const productData = {
                ...product,
                productType: inferredProductType,
            };

            // Update the measurement with selected product and full selections
            const response = await axios.put(`/api/measurements/${measurementId}`, {
                selectedProduct: productData,
                notes: internalNotes,
                order: {
                    ...(currentMeasurement?.order || {}),
                    orderNote: productNotes,
                },
                selections: persistedSelections,
                completedSteps: ['patientInfo', 'measurements', 'productSelection'],
            });

            setSelections(response.data?.selections || persistedSelections);

            // Update the currentMeasurement state to enable the Order button
            setCurrentMeasurement(response.data);
            setProductNotes(response.data?.order?.orderNote ?? productNotes);

            setSuccess('Product selected and saved successfully!');
            // Stay on this page instead of navigating away
        } catch (error) {
            console.error('Error saving product selection:', error);
            setError(t('measurements.stockingSelection.saveError'));
        }
    };

    const currentStepData = visibleWorkflowSteps[currentStep];
    const availableOptions = currentStepData ? getAvailableOptions(currentStepData.id) : [];
    const isStepComplete = currentStepData ? selections[currentStepData.id] !== undefined : false;
    const hasNotesChanged = (productNotes || '') !== (currentMeasurement?.order?.orderNote || '');
    const displayedMatchingProducts = useMemo(() => {
        if (isMadeToMeasureSelectedProduct && selectedProductVendorTableId) {
            const filteredProducts = matchingProducts.filter((product) =>
                product.vendorTableID === selectedProductVendorTableId || product.vendorTableId === selectedProductVendorTableId
            );

            if (filteredProducts.length > 0) {
                return filteredProducts;
            }
        }

        // In ordered/read-only made-to-measure flows we still need one product card
        // so that the "View Order" action remains available.
        if (
            matchingProducts.length === 0
            && (isOrdered || isReadOnly)
            && isMadeToMeasureMeasurement
            && selectedProduct
        ) {
            return [selectedProduct];
        }

        return matchingProducts;
    }, [
        matchingProducts,
        isMadeToMeasureSelectedProduct,
        selectedProductVendorTableId,
        isOrdered,
        isReadOnly,
        isMadeToMeasureMeasurement,
        selectedProduct,
    ]);

    const displayedSearchMatchingProducts = useMemo(() => {
        const products = location.state?.matchingProducts || [];

        if (isMadeToMeasureSelectedProduct && selectedProductVendorTableId) {
            const filteredProducts = products.filter((product) =>
                product.vendorTableID === selectedProductVendorTableId || product.vendorTableId === selectedProductVendorTableId
            );

            if (filteredProducts.length > 0) {
                return filteredProducts;
            }
        }

        return products;
    }, [location.state?.matchingProducts, isMadeToMeasureSelectedProduct, selectedProductVendorTableId]);

    // Check if all visible workflow steps have been completed
    const allStepsComplete = visibleWorkflowSteps.every(step => selections[step.id] !== undefined);

    if ((isOffline || isApiUnavailable) && !canRenderOfflineSelection) {
        return (
            <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-xl rounded-2xl border border-yellow-200 bg-white p-8 shadow-lg">
                    <div className="mb-4 flex items-center gap-3 text-yellow-700">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h1 className="text-xl font-semibold">{t('measurements.stockingSelection.offlineUnavailableTitle') || 'Stocking selection is unavailable offline'}</h1>
                    </div>
                    <p className="text-sm text-gray-700">
                        {t('measurements.stockingSelection.offlineUnavailableText') || 'This page requires a live connection and will not reuse cached data when the browser is offline.'}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/patients')}
                            className="btn-primary"
                        >
                            {t('common.back')}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="btn-secondary"
                        >
                            {t('common.retry') || 'Retry'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-transition min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4 max-w-6xl">
                {/* Header */}
                <div className="mb-8">
                    {/* Progress Indicator */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between text-sm max-w-2xl mx-auto">
                            <div className="flex items-center text-green-600 font-semibold">
                                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span>{t('patientInfo.progress.step1')}</span>
                            </div>
                            <div className="flex-1 h-1 bg-green-600 mx-4"></div>
                            <div className="flex items-center text-green-600 font-semibold">
                                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span>{t('patientInfo.progress.step2')}</span>
                            </div>
                            <div className="flex-1 h-1 bg-green-600 mx-4"></div>
                            <div className={`flex items-center ${isReadOnly ? 'text-blue-600' : (isOrdered ? 'text-green-600' : 'text-blue-600')} font-semibold`}>
                                <div className={`w-8 h-8 ${isReadOnly ? 'bg-blue-600' : (isOrdered ? 'bg-green-600' : 'bg-blue-600')} text-white rounded-full flex items-center justify-center mr-2`}>
                                    {isReadOnly ? (
                                        '3'
                                    ) : isOrdered ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        '3'
                                    )}
                                </div>
                                <span>{t('patientInfo.progress.step3')}</span>
                            </div>
                            <div className={`flex-1 h-1 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>
                            <div className={`flex items-center ${isOrdered ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                                <div className={`w-8 h-8 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} ${isOrdered ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                    {isOrdered ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        '4'
                                    )}
                                </div>
                                <span>{t('patientInfo.progress.step4')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Read-only mode indicator */}
                    {isReadOnly && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-yellow-800 font-semibold">
                                    {t('patientInfo.readOnlyWarning')}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-3xl font-bold">{t('measurements.stockingSelection.title')}</h1>
                        <button
                            onClick={() => navigate('/patients')}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {measurements && (
                        <PatientInfoHeader
                            patientFirstName={currentMeasurement?.patientFirstName || location.state?.patientFirstName}
                            patientLastName={currentMeasurement?.patientLastName || location.state?.patientLastName}
                            patientNameFallback={location.state?.patientName || t('measurements.stockingSelection.unknown')}
                            patientId={currentMeasurement?.patientId || location.state?.patientId || t('measurements.stockingSelection.notAvailable')}
                            lengthType={lengthType}
                            compressionClass={compressionClass}
                            knitting={knitting}
                            measurementType={currentMeasurement?.measurementType || location.state?.measurementType || ''}
                        >

                            {displayedSearchMatchingProducts.length > 0 && !hideMatchingProductsBox && (
                                <div className="mt-3 pt-3 border-t border-blue-300">
                                    <p className="text-sm font-semibold text-blue-900 mb-2">
                                        {t('measurements.stockingSelection.matchingProductsCount', { count: displayedSearchMatchingProducts.length })}:
                                    </p>
                                    <p className="text-xs text-blue-700 mb-3 italic">
                                        {t('measurements.stockingSelection.clickToSelect') || 'Click on a product to select it and begin customization'}
                                    </p>
                                    <div className="space-y-4">
                                        {groupMatchingProducts(displayedSearchMatchingProducts).map((vendorGroup, vendorIndex) => {
                                            const isSelected = selections.vendor === vendorGroup.vendorName &&
                                                selections.vendortype === vendorGroup.vendorType;
                                            const isClickable = !isReadOnly && !isOrdered;
                                            const vendorSectionKey = `${vendorGroup.vendorName || 'vendor'}-${vendorGroup.vendorType || 'type'}-${vendorIndex}`;
                                            const isVendorSectionExpanded = !!expandedVendorSections[vendorSectionKey];
                                            const vendorHasPerfectMatch = vendorGroup.widthGroups.some(
                                                (widthGroup) => calculateSizeDeviation(widthGroup) === 0
                                            );
                                            // Calculate correct total deviation accounting for both legs
                                            return (
                                                <div
                                                    key={vendorIndex}
                                                    className={`bg-white rounded-lg border-2 transition-all ${isSelected
                                                        ? 'border-blue-600 shadow-md'
                                                        : 'border-blue-200'
                                                        }`}
                                                >
                                                    {/* Vendor Header */}
                                                    <div
                                                        className={`p-3 border-b border-gray-200 ${isClickable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                                        onClick={() => isClickable && handleMatchingProductClick(vendorGroup.product)}
                                                    >
                                                        <div className="flex justify-between items-center">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-base font-bold text-gray-900">
                                                                    {vendorGroup.vendorName}
                                                                </p>
                                                                {isSelected && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                                                                        {t('measurements.stockingSelection.selected') || 'Selected'}
                                                                    </span>
                                                                )}
                                                                {isClickable && !isSelected && (
                                                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleVendorSection(vendorSectionKey);
                                                                }}
                                                                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${!isVendorSectionExpanded && vendorHasPerfectMatch
                                                                    ? 'border border-green-200 bg-green-100 text-green-800 hover:bg-green-200'
                                                                    : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                <span>
                                                                    {isVendorSectionExpanded
                                                                        ? (t('common.hideDetails') || 'Hide Details')
                                                                        : vendorHasPerfectMatch
                                                                            ? (t('common.match100') || '100% Match')
                                                                            : (t('common.showDeviations') || 'Show Deviations')}
                                                                </span>
                                                                <svg
                                                                    className={`w-3 h-3 transition-transform ${isVendorSectionExpanded ? 'rotate-180' : ''}`}
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        {vendorGroup.vendorType && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                Type: {vendorGroup.vendorType}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Width Groups - Expanded only when user clicks the expand button */}
                                                    {isVendorSectionExpanded && (
                                                        <div className="p-3 space-y-3">
                                                            {vendorGroup.widthGroups.map((widthGroup, widthIndex) => {
                                                                const sizeDev = calculateSizeDeviation(widthGroup);
                                                                return (
                                                                    <div key={widthIndex} className="bg-gray-50 rounded-md p-3">
                                                                        <div className="flex justify-between items-center mb-2">
                                                                            <div className="font-semibold text-sm text-gray-800">
                                                                                Size: {widthGroup.modelName}
                                                                            </div>
                                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${sizeDev > 0
                                                                                ? 'bg-orange-100 text-orange-800'
                                                                                : 'bg-green-100 text-green-800'
                                                                                }`}>
                                                                                {sizeDev === 0
                                                                                    ? `100% Match`
                                                                                    : `${sizeDev.toFixed(1)}cm Deviation`
                                                                                }
                                                                            </span>
                                                                        </div>

                                                                        {/* Left and Right Legs */}
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                            {/* Render legs in correct order based on swapLegSides preference */}
                                                                            {(() => {
                                                                                const swapLegs = user?.preferences?.swapLegSides || false;
                                                                                const leftLegComponent = widthGroup.legs.left && (
                                                                                    <div className="bg-white rounded p-3 border border-gray-200">
                                                                                        <div className="flex items-center gap-2 mb-2">
                                                                                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                                                                            <span className="text-xs font-semibold text-gray-700">Left Leg</span>
                                                                                        </div>
                                                                                        <div className="text-xs text-gray-600 space-y-1">
                                                                                            <div>
                                                                                                <span className="font-medium">Width:</span> {widthGroup.legs.left.vendorModel}
                                                                                            </div>
                                                                                            {widthGroup.lengths && widthGroup.lengths.length > 0 && (
                                                                                                <div>
                                                                                                    <span className="font-medium">Length:</span>{' '}
                                                                                                    {widthGroup.lengths.map(l => l.vendorModel).join(', ')}
                                                                                                </div>
                                                                                            )}
                                                                                            {widthGroup.legs.left.totalDevWidthsL > 0 && (
                                                                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                                                                    <span className="text-orange-600 font-medium">
                                                                                                        Width Deviation: {widthGroup.legs.left.totalDevWidthsL.toFixed(1)}cm
                                                                                                    </span>
                                                                                                </div>
                                                                                            )}
                                                                                            {/*                                                                                             {widthGroup.lengths && widthGroup.lengths.some(l => l.totalDevLengths > 0) && (
                                                                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                                                                    <span className="text-orange-600 font-medium">
                                                                                                        Length Deviation: {widthGroup.lengths.find(l => l.totalDevLengths > 0)?.totalDevLengths.toFixed(1)}cm
                                                                                                    </span>
                                                                                                </div>
                                                                                            )}
 */}                                                                                        </div>

                                                                                        {/* Left Leg Width Deviations */}
                                                                                        {widthGroup.legs.left.totalDevWidthsL > 0 && (() => {
                                                                                            const leftDeviations = getWidthDeviationDetails(widthGroup.legs.left, 'left');
                                                                                            const expandKey = `${vendorIndex}-${widthIndex}-left-width`;
                                                                                            const isExpanded = expandedDeviations[expandKey];

                                                                                            return leftDeviations.length > 0 ? (
                                                                                                <div className="mt-2">
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            toggleDeviationExpansion(expandKey);
                                                                                                        }}
                                                                                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                                                                    >
                                                                                                        <span>View Details</span>
                                                                                                        <svg
                                                                                                            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                                                                            fill="none"
                                                                                                            stroke="currentColor"
                                                                                                            viewBox="0 0 24 24"
                                                                                                        >
                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                        </svg>
                                                                                                    </button>

                                                                                                    {isExpanded && (
                                                                                                        <div className="mt-2 space-y-2">
                                                                                                            {leftDeviations.map((detail, idx) => (
                                                                                                                <div key={idx} className="bg-orange-50 rounded p-2 text-xs">
                                                                                                                    <div className="font-semibold text-gray-800 mb-1">
                                                                                                                        {detail.fieldName}
                                                                                                                    </div>
                                                                                                                    <div className="space-y-1 text-gray-700">
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.sizeRange')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.minValue?.toFixed(1)} - {detail.maxValue?.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.measured')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.measuredValue !== null ? `${detail.measuredValue.toFixed(1)} cm` : 'N/A'}
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.deviation')}:</span>
                                                                                                                            <span className={`ml-1 font-bold ${detail.deviation > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                                                                                                                                {detail.deviation > 0 ? '+' : ''}{detail.deviation.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                            <span className="ml-1 text-gray-600">
                                                                                                                                ({detail.deviation > 0 ? 'Too small' : 'Too large'})
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })()}

                                                                                        {/* Left Leg Length Deviations */}
                                                                                        {/* {widthGroup.lengths && widthGroup.lengths.some(l => l.totalDevLengths > 0) && (() => {
                                                                                            const lengthWithDev = widthGroup.lengths.find(l => l.totalDevLengths > 0);
                                                                                            if (!lengthWithDev) return null;

                                                                                            const lengthDeviations = getWidthDeviationDetails(lengthWithDev, 'left');
                                                                                            const expandKey = `${vendorIndex}-${widthIndex}-left-length`;
                                                                                            const isExpanded = expandedDeviations[expandKey];

                                                                                            return lengthDeviations.length > 0 ? (
                                                                                                <div className="mt-2">
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            toggleDeviationExpansion(expandKey);
                                                                                                        }}
                                                                                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                                                                    >
                                                                                                        <span>View Length Details</span>
                                                                                                        <svg
                                                                                                            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                                                                            fill="none"
                                                                                                            stroke="currentColor"
                                                                                                            viewBox="0 0 24 24"
                                                                                                        >
                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                        </svg>
                                                                                                    </button>

                                                                                                    {isExpanded && (
                                                                                                        <div className="mt-2 space-y-2">
                                                                                                            {lengthDeviations.map((detail, idx) => (
                                                                                                                <div key={idx} className="bg-orange-50 rounded p-2 text-xs">
                                                                                                                    <div className="font-semibold text-gray-800 mb-1">
                                                                                                                        {detail.fieldName}
                                                                                                                    </div>
                                                                                                                    <div className="space-y-1 text-gray-700">
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.sizeRange')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.minValue?.toFixed(1)} - {detail.maxValue?.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.measured')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.measuredValue !== null ? `${detail.measuredValue.toFixed(1)} cm` : 'N/A'}
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.deviation')}:</span>
                                                                                                                            <span className={`ml-1 font-bold ${detail.deviation > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                                                                                                                                {detail.deviation > 0 ? '+' : ''}{detail.deviation.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                            <span className="ml-1 text-gray-600">
                                                                                                                                ({detail.deviation > 0 ? 'Too small' : 'Too large'})
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })()} */}
                                                                                    </div>
                                                                                );

                                                                                const rightLegComponent = widthGroup.legs.right && (
                                                                                    <div className="bg-white rounded p-3 border border-gray-200">
                                                                                        <div className="flex items-center gap-2 mb-2">
                                                                                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                                                                            <span className="text-xs font-semibold text-gray-700">Right Leg</span>
                                                                                        </div>
                                                                                        <div className="text-xs text-gray-600 space-y-1">
                                                                                            <div>
                                                                                                <span className="font-medium">Width:</span> {widthGroup.legs.right.vendorModel}
                                                                                            </div>
                                                                                            {widthGroup.lengths && widthGroup.lengths.length > 0 && (
                                                                                                <div>
                                                                                                    <span className="font-medium">Length:</span>{' '}
                                                                                                    {widthGroup.lengths.map(l => l.vendorModel).join(', ')}
                                                                                                </div>
                                                                                            )}
                                                                                            {widthGroup.legs.right.totalDevWidthsR > 0 && (
                                                                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                                                                    <span className="text-orange-600 font-medium">
                                                                                                        Width Deviation: {widthGroup.legs.right.totalDevWidthsR.toFixed(1)}cm
                                                                                                    </span>
                                                                                                </div>
                                                                                            )}
                                                                                            {/*                                                                                             {widthGroup.lengths && widthGroup.lengths.some(l => l.totalDevLengths > 0) && (
                                                                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                                                                    <span className="text-orange-600 font-medium">
                                                                                                        Length Deviation: {widthGroup.lengths.find(l => l.totalDevLengths > 0)?.totalDevLengths.toFixed(1)}cm
                                                                                                    </span>
                                                                                                </div>
                                                                                            )} */}
                                                                                        </div>

                                                                                        {/* Right Leg Width Deviations */}
                                                                                        {widthGroup.legs.right.totalDevWidthsR > 0 && (() => {
                                                                                            const rightDeviations = getWidthDeviationDetails(widthGroup.legs.right, 'right');
                                                                                            const expandKey = `${vendorIndex}-${widthIndex}-right-width`;
                                                                                            const isExpanded = expandedDeviations[expandKey];

                                                                                            return rightDeviations.length > 0 ? (
                                                                                                <div className="mt-2">
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            toggleDeviationExpansion(expandKey);
                                                                                                        }}
                                                                                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                                                                    >
                                                                                                        <span>View Width Details</span>
                                                                                                        <svg
                                                                                                            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                                                                            fill="none"
                                                                                                            stroke="currentColor"
                                                                                                            viewBox="0 0 24 24"
                                                                                                        >
                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                        </svg>
                                                                                                    </button>

                                                                                                    {isExpanded && (
                                                                                                        <div className="mt-2 space-y-2">
                                                                                                            {rightDeviations.map((detail, idx) => (
                                                                                                                <div key={idx} className="bg-orange-50 rounded p-2 text-xs">
                                                                                                                    <div className="font-semibold text-gray-800 mb-1">
                                                                                                                        {detail.fieldName}
                                                                                                                    </div>
                                                                                                                    <div className="space-y-1 text-gray-700">
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.sizeRange')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.minValue?.toFixed(1)} - {detail.maxValue?.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.measured')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.measuredValue !== null ? `${detail.measuredValue.toFixed(1)} cm` : 'N/A'}
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.deviation')}:</span>
                                                                                                                            <span className={`ml-1 font-bold ${detail.deviation > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                                                                                                                                {detail.deviation > 0 ? '+' : ''}{detail.deviation.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                            <span className="ml-1 text-gray-600">
                                                                                                                                ({detail.deviation > 0 ? 'Too small' : 'Too large'})
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })()}

                                                                                        {/* Right Leg Length Deviations */}
                                                                                        {/* {widthGroup.lengths && widthGroup.lengths.some(l => l.totalDevLengths > 0) && (() => {
                                                                                            const lengthWithDev = widthGroup.lengths.find(l => l.totalDevLengths > 0);
                                                                                            if (!lengthWithDev) return null;

                                                                                            const lengthDeviations = getWidthDeviationDetails(lengthWithDev, 'right');
                                                                                            const expandKey = `${vendorIndex}-${widthIndex}-right-length`;
                                                                                            const isExpanded = expandedDeviations[expandKey];

                                                                                            return lengthDeviations.length > 0 ? (
                                                                                                <div className="mt-2">
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            toggleDeviationExpansion(expandKey);
                                                                                                        }}
                                                                                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                                                                    >
                                                                                                        <span>View Length Details</span>
                                                                                                        <svg
                                                                                                            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                                                                            fill="none"
                                                                                                            stroke="currentColor"
                                                                                                            viewBox="0 0 24 24"
                                                                                                        >
                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                        </svg>
                                                                                                    </button>

                                                                                                    {isExpanded && (
                                                                                                        <div className="mt-2 space-y-2">
                                                                                                            {lengthDeviations.map((detail, idx) => (
                                                                                                                <div key={idx} className="bg-orange-50 rounded p-2 text-xs">
                                                                                                                    <div className="font-semibold text-gray-800 mb-1">
                                                                                                                        {detail.fieldName}
                                                                                                                    </div>
                                                                                                                    <div className="space-y-1 text-gray-700">
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.sizeRange')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.minValue?.toFixed(1)} - {detail.maxValue?.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.measured')}:</span>
                                                                                                                            <span className="ml-1 font-medium">
                                                                                                                                {detail.measuredValue !== null ? `${detail.measuredValue.toFixed(1)} cm` : 'N/A'}
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                        <div>
                                                                                                                            <span className="text-gray-600">{t('measurements.stockingSelection.deviation')}:</span>
                                                                                                                            <span className={`ml-1 font-bold ${detail.deviation > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                                                                                                                                {detail.deviation > 0 ? '+' : ''}{detail.deviation.toFixed(1)} cm
                                                                                                                            </span>
                                                                                                                            <span className="ml-1 text-gray-600">
                                                                                                                                ({detail.deviation > 0 ? 'Too small' : 'Too large'})
                                                                                                                            </span>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })()} */}
                                                                                    </div>
                                                                                );

                                                                                // Render in correct order: left first (unless swapLegs is true)
                                                                                if (swapLegs) {
                                                                                    return (
                                                                                        <>
                                                                                            {rightLegComponent}
                                                                                            {leftLegComponent}
                                                                                        </>
                                                                                    );
                                                                                } else {
                                                                                    return (
                                                                                        <>
                                                                                            {leftLegComponent}
                                                                                            {rightLegComponent}
                                                                                        </>
                                                                                    );
                                                                                }
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </PatientInfoHeader>
                    )}

                    {isMadeToMeasureMeasurement && (
                        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                            <h3 className="text-sm font-semibold text-blue-900 mb-2">
                                {t('measurements.stockingSelection.madeToMeasureSummaryTitle') || 'Made-to-Measure Summary'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div>
                                    <span className="font-medium text-blue-800">{t('measurements.stockingSelection.measurementTypeLabel') || 'Measurement Type'}:</span>
                                    <p className="text-blue-900">{t('measurements.legMeasuring.measurementType.madeToMeasure') || 'Made-to-Measure'}</p>
                                </div>
                                <div>
                                    <span className="font-medium text-blue-800">{t('measurements.stockingSelection.vendorLabel') || 'Vendor'}:</span>
                                    <p className="text-blue-900">{selectedProductVendorName}</p>
                                </div>
                                <div>
                                    <span className="font-medium text-blue-800">{t('measurements.stockingSelection.modelLabel') || 'Model'}:</span>
                                    <p className="text-blue-900">{selectedProductModelName}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Measurement Summary - Read-only reminder between vendor selection and options */}
                {(measurements || currentMeasurement?.measurements) && selections.vendor && (() => {
                    const hmMeasurements = currentMeasurement?.measurements || measurements || {};
                    const zmMeasurements = {
                        ...(location.state?.measurements2 || {}),
                        ...(currentMeasurement?.measurements2 || {}),
                    };

                    // Helper function to categorize measurements
                    const categorizeMeasurements = (sourceMeasurements = {}) => {
                        const leftLeg = [];
                        const rightLeg = [];
                        const length = [];
                        const other = [];

                        Object.entries(sourceMeasurements).forEach(([key, value]) => {
                            // Skip internal fields, null/undefined values, and non-measurement fields
                            if (key.startsWith('_') || value === null || value === undefined ||
                                key === 'id' || key === 'lengthType' || key === 'compressionClass' ||
                                key === 'knitting' || key === 'notes') {
                                return;
                            }

                            // Categorize based on key pattern matching LegMeasuring.js structure
                            // Right leg measurements end with 'R': cGR, cFR, cER, cDR, cCR, cB1R, cBR, cYR, cAR
                            // Left leg measurements end with 'L': cGL, cFL, cEL, cDL, cCL, cB1L, cBL, cYL, cAL
                            // Length measurements start with 'l': lT, lH, lG, lF, lE, lD, lC, lB1, lB, lA, lZ
                            // Hip measurements: cT, cH
                            // Shoe size: sS

                            if (key.endsWith('R')) {
                                // Right leg measurements (cGR, cFR, cAR, etc.)
                                rightLeg.push([key, value]);
                            } else if (key.endsWith('L')) {
                                // Left leg measurements (cGL, cFL, cAL, etc.)
                                leftLeg.push([key, value]);
                            } else if (key.startsWith('l') || key === 'sS') {
                                // Length measurements (lG, lF, lB, etc.) or shoe size
                                length.push([key, value]);
                            } else if (key === 'cT' || key === 'cH') {
                                // Hip/waist measurements - add to other section
                                other.push([key, value]);
                            } else {
                                // Other measurements or unclear categorization
                                other.push([key, value]);
                            }
                        });

                        return { leftLeg, rightLeg, length, other };
                    };

                    const hmCategorized = categorizeMeasurements(hmMeasurements);
                    const zmCategorized = categorizeMeasurements(zmMeasurements);

                    // Define measurement order based on LegMeasuring.js structure (top to bottom)
                    const rightLegOrder = ['cGR', 'cFR', 'cER', 'cDR', 'cCR', 'cB1R', 'cBR', 'cYR', 'cAR'];
                    const leftLegOrder = ['cGL', 'cFL', 'cEL', 'cDL', 'cCL', 'cB1L', 'cBL', 'cYL', 'cAL'];
                    const lengthOrder = ['lT', 'lH', 'lG', 'lF', 'lE', 'lD', 'lC', 'lB1', 'lB', 'lA', 'lZ', 'sS'];

                    // Sort measurements according to defined order
                    const sortByOrder = (measurements, order) => {
                        return measurements.sort((a, b) => {
                            const indexA = order.indexOf(a[0]);
                            const indexB = order.indexOf(b[0]);
                            // If not in order array, put at end
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;
                            return indexA - indexB;
                        });
                    };

                    const sortCategorized = ({ leftLeg, rightLeg, length, other }) => ({
                        sortedRightLeg: sortByOrder([...rightLeg], rightLegOrder),
                        sortedLeftLeg: sortByOrder([...leftLeg], leftLegOrder),
                        sortedLength: sortByOrder([...length], lengthOrder),
                        sortedOther: other,
                    });

                    const hmSorted = sortCategorized(hmCategorized);
                    const zmSorted = sortCategorized(zmCategorized);

                    const formatFieldLabel = (key) => (key.endsWith('L') || key.endsWith('R') ? key.slice(0, -1) : key);

                    const toRowMap = (entries = []) => {
                        const map = new Map();
                        entries.forEach(([key, value]) => {
                            map.set(key, value);
                        });
                        return map;
                    };

                    const buildComparisonRows = (hmEntries = [], zmEntries = []) => {
                        const hmMap = toRowMap(hmEntries);
                        const zmMap = toRowMap(zmEntries);
                        const orderedKeys = [
                            ...hmEntries.map(([key]) => key),
                            ...zmEntries.map(([key]) => key).filter((key) => !hmMap.has(key)),
                        ];

                        return orderedKeys.map((key) => ({
                            key,
                            label: formatFieldLabel(key),
                            hm: hmMap.get(key),
                            zm: zmMap.get(key),
                        }));
                    };

                    const leftLegRows = buildComparisonRows(
                        hmSorted.sortedLeftLeg,
                        zmSorted.sortedLeftLeg,
                    );
                    const lengthRows = buildComparisonRows(hmSorted.sortedLength, zmSorted.sortedLength);
                    const rightLegRows = buildComparisonRows(
                        hmSorted.sortedRightLeg,
                        zmSorted.sortedRightLeg,
                    );
                    const otherRows = buildComparisonRows(hmSorted.sortedOther, zmSorted.sortedOther);

                    const showZmColumn = Object.values(zmMeasurements).some((value) => (
                        value !== undefined && value !== null && value !== ''
                    ));

                    const renderComparisonTable = (sectionTitle, rows) => {
                        if (rows.length === 0) {
                            return (
                                <div className="mb-4">
                                    <h4 className="text-sm font-semibold mb-2 text-gray-700">{sectionTitle}</h4>
                                    <div className="text-xs text-gray-500 italic">
                                        {t('measurements.noData') || 'No measurements'}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div className="mb-4 overflow-x-auto">
                                <h4 className="text-sm font-semibold mb-2 text-gray-700">{sectionTitle}</h4>
                                <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden bg-white">
                                    <thead className="bg-gray-100 text-gray-700">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-semibold border-b border-gray-200">Field</th>
                                            <th className="px-3 py-2 text-left font-semibold border-b border-gray-200">HM</th>
                                            {showZmColumn && (
                                                <th className="px-3 py-2 text-left font-semibold border-b border-gray-200">ZM</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={`${sectionTitle}-${row.key}`} className="border-b border-gray-100 last:border-b-0">
                                                <td className="px-3 py-2 font-medium text-gray-700">{row.label}</td>
                                                <td className="px-3 py-2 text-blue-700">{row.hm ?? '-'}</td>
                                                {showZmColumn && (
                                                    <td className="px-3 py-2 text-indigo-700">{row.zm ?? '-'}</td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    };

                    return (
                        <div className="card mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t('measurements.stockingSelection.measurementSummary') || 'Measurement Summary'}
                            </h3>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    {renderComparisonTable(t('measurements.legMeasuring.leftLeg') || 'Left Leg', leftLegRows)}
                                    {renderComparisonTable(t('legMeasuringPage.lengthMeasurements') || 'Length Measurements', lengthRows)}
                                    {renderComparisonTable(t('measurements.legMeasuring.rightLeg') || 'Right Leg', rightLegRows)}
                                </div>
                                <div className="mt-2">
                                    {renderComparisonTable(t('measurements.other') || 'Other Measurements', otherRows)}
                                </div>
                            </div>

                            {/* Additional context information */}
                            <div className="mt-4 pt-4 border-t border-gray-300">
                                <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                                    {lengthType && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium">{t('measurements.lengthType.label')}:</span>
                                            <span>{`${lengthType} (${t(`measurements.lengthType.${lengthType}`)})`}</span>
                                        </div>
                                    )}
                                    {compressionClass && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium">{t('measurements.compressionClass.label')}:</span>
                                            <span>{t(`measurements.compressionClass.${compressionClass}`)}</span>
                                        </div>
                                    )}
                                    {knitting && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium">{t('measurements.knitting.label')}:</span>
                                            <span>{t(`measurements.knitting.${knitting}`)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {displayedMatchingProducts.length === 0 ? (
                    <>
                        {visibleWorkflowSteps.length === 0 ? (
                            <div className="card mb-6" ref={workflowSectionRef}>
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-lg mb-2">
                                        {hasSearchMatchingProducts
                                            ? t('measurements.stockingSelection.noOptionsTitle')
                                            : t('measurements.stockingSelection.noMatchingProductTitle', { defaultValue: 'No matching product available' })}
                                    </p>
                                    <p className="text-sm">
                                        {hasSearchMatchingProducts
                                            ? t('measurements.stockingSelection.selectMatchingProductFirst', { defaultValue: 'Please select a Matching Product first.' })
                                            : t('measurements.stockingSelection.noOptionsText')}
                                    </p>
                                </div>
                                {canShowMadeToMeasureFallback && !hasSearchMatchingProducts && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-blue-800 mb-3 text-center">
                                            {t('measurements.stockingSelection.noOffTheShelf')}
                                        </p>
                                        <div className="flex justify-center">
                                            <button
                                                onClick={handleNavigateToMadeToMeasure}
                                                className="btn-primary"
                                            >
                                                {t('measurements.stockingSelection.continueMadeToMeasure')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {!canShowMadeToMeasureFallback && isBasicSubscription && !hasSearchMatchingProducts && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-yellow-800 mb-3 text-center">
                                            {t('measurements.stockingSelection.upgradeForFeature')}
                                        </p>
                                        <div className="flex justify-center">
                                            <button
                                                onClick={handleNavigateToUpgrade}
                                                className="btn-primary"
                                            >
                                                {t('measurements.stockingSelection.upgradeToPro')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-between mt-6">
                                    <button
                                        onClick={handlePrevious}
                                        className="btn-secondary"
                                    >
                                        {t('common.back')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Progress Bar */}
                                <div className="mb-8" ref={workflowSectionRef}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            Step {currentStep + 1} of {visibleWorkflowSteps.length}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {Math.round(((currentStep + 1) / visibleWorkflowSteps.length) * 100)}% Complete
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${((currentStep + 1) / visibleWorkflowSteps.length) * 100}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Selection Steps Overview */}
                                <div className="card mb-6">
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                        {visibleWorkflowSteps.map((step, index) => (
                                            <div
                                                key={step.id}
                                                className={`text-center p-2 rounded-lg cursor-pointer transition-colors ${index === currentStep
                                                    ? 'bg-blue-100 border-2 border-blue-500'
                                                    : selections[step.id]
                                                        ? 'bg-green-100 border border-green-300'
                                                        : 'bg-gray-100 border border-gray-200'
                                                    }`}
                                                onClick={() => setCurrentStep(index)}
                                            >
                                                <div className="text-xs font-medium mb-1">{step.label}</div>
                                                {selections[step.id] && (
                                                    <div className="text-xs text-gray-600 truncate">
                                                        {getSelectionLabel(step.id, selections[step.id])}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Current Step */}
                                {currentStepData && (
                                    <div className="card mb-6">
                                        <h2 className="text-2xl font-semibold mb-6">{currentStepData.label}</h2>

                                        {currentStepData.id === 'productType' && !hasFeatureAccess('made-to-measure') && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                                <p className="text-sm text-yellow-800">
                                                    <strong>{t('measurements.stockingSelection.noteLabel')}:</strong> {t('measurements.stockingSelection.proRequired')}
                                                </p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {availableOptions.length > 0 ? (
                                                availableOptions.map((option) => (
                                                    <button
                                                        key={option.id || option}
                                                        onClick={() => handleSelection(currentStepData.id, option.value || option)}
                                                        disabled={
                                                            isReadOnly ||
                                                            (currentStepData.id === 'productType' &&
                                                                option === 'made-to-measure' &&
                                                                !hasFeatureAccess('made-to-measure'))
                                                        }
                                                        className={`p-4 rounded-lg border-2 transition-all ${selections[currentStepData.id] === (option.value || option)
                                                            ? 'border-blue-600 bg-blue-50'
                                                            : 'border-gray-200 hover:border-blue-300 bg-white'
                                                            } ${(currentStepData.id === 'productType' &&
                                                                option === 'made-to-measure' &&
                                                                !hasFeatureAccess('made-to-measure')) || isReadOnly
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : ''
                                                            }`}
                                                    >
                                                        <div className="font-semibold">{option.label || option}</div>
                                                        {option.id && currentStepData.id === 'vendor' && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Click to select
                                                            </div>
                                                        )}
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="col-span-full text-center py-8 text-gray-500">
                                                    {t('measurements.stockingSelection.completePrevious')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Navigation Buttons */}
                                <div className="flex justify-between">
                                    <button
                                        onClick={handlePrevious}
                                        className="btn-secondary"
                                    >
                                        {t('common.back')}
                                    </button>

                                    <button
                                        onClick={handleNext}
                                        disabled={!allStepsComplete || isReadOnly}
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {currentStep === visibleWorkflowSteps.length - 1 ? t('measurements.stockingSelection.selectProduct') : t('common.next')}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    /* Matching Products Results */
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-semibold">
                                {t('measurements.stockingSelection.selectedProduct')}:
                            </h2>
                            {!isReadOnly && !isOrdered && (
                                <button
                                    onClick={async () => {
                                        setMatchingProducts([]);
                                        setCurrentStep(0);
                                        setSelections({});
                                        setHideMatchingProductsBox(false);

                                        // Clear the saved selection from the database
                                        const measurementId = location.state?.measurementId || currentMeasurement?._id;
                                        if (measurementId) {
                                            try {
                                                const response = await axios.put(`/api/measurements/${measurementId}`, {
                                                    selectedProduct: null,
                                                    selections: {},
                                                    completedSteps: ['patientInfo', 'measurements'],
                                                });
                                                setCurrentMeasurement(response.data);
                                            } catch (error) {
                                                console.error('Error clearing selection:', error);
                                            }
                                        }
                                    }}
                                    className="btn-secondary"
                                >
                                    {t('measurements.stockingSelection.startNewSelection')}
                                </button>
                            )}
                        </div>

                        {displayedMatchingProducts.length > 1 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                <p className="text-green-800">
                                    <strong>{t('measurements.stockingSelection.vendorNeutralLabel')}:</strong> {t('measurements.stockingSelection.vendorNeutralText', { count: displayedMatchingProducts.length })}
                                </p>
                            </div>
                        )}

                        <div className="card">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-stretch">
                                <div className="lg:col-span-8">
                                    <div className={`grid grid-cols-1 ${displayedMatchingProducts.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
                                        {displayedMatchingProducts.map((product, index) => {
                                            const savedProduct = currentMeasurement?.selectedProduct;
                                            const savedVendorTableId = savedProduct?.vendorTableID || savedProduct?.vendorTableId || '';
                                            const currentVendorTableId = product?.vendorTableID || product?.vendorTableId || selections.vendorTableId || '';
                                            const isSavedProductMatch = !!savedProduct
                                                && (savedProduct.vendorName || '') === (product.vendorName || selections.vendor || '')
                                                && (savedProduct.modelId || savedProduct.modelName || '') === (product.modelId || product.modelName || selections.model || '')
                                                && (!savedVendorTableId || !currentVendorTableId || savedVendorTableId === currentVendorTableId);
                                            const shouldShowSaveSelection = !isReadOnly && !isOrdered && (!isSavedProductMatch || hasNotesChanged);
                                            const canOrderThisProduct = isSavedProductMatch && !hasNotesChanged;

                                            // Merge product properties with selections to ensure all options are displayed
                                            const displayData = {
                                                ...product,
                                                // Override with selections if they exist (for read-only mode)
                                                // Note: vendortype (lowercase) maps to vendorType (camelCase)
                                                ...(selections.vendor && { vendor: selections.vendor }),
                                                ...(selections.vendortype && { vendorType: selections.vendortype }),
                                                ...(selections.model && { model: selections.model }),
                                                ...(selections.size && { size: selections.size }),
                                                ...(selections.lengthsize && { lengthsize: selections.lengthsize }),
                                                ...(selections.leftright && { leftright: selections.leftright }),
                                                ...(selections.toeOption && { toeOption: selections.toeOption }),
                                                ...(selections.footLength && { footLength: selections.footLength }),
                                                ...(selections.bandType && { bandType: selections.bandType }),
                                                ...(selections.bandWidth && { bandWidth: selections.bandWidth }),
                                                ...(selections.ankle && { ankle: selections.ankle }),
                                                ...(selections.color && { color: selections.color }),
                                                ...(selections.design && { design: selections.design }),
                                                ...(selections.fabric && { fabric: selections.fabric }),
                                                ...(selections.pantyShortCompression && { pantyShortCompression: selections.pantyShortCompression }),
                                                ...(selections.pantyShortSize && { pantyShortSize: selections.pantyShortSize }),
                                                ...(selections.pantyCrotch && { pantyCrotch: selections.pantyCrotch }),
                                            };

                                            // Get model name if we have a model selection
                                            let modelDisplayName = displayData.modelName;
                                            if (selections.model && productConfig) {
                                                const vendor = productConfig.vendorTables.find(v => v.id === selections.vendor);
                                                const model = vendor?.models.find(m => m.id === selections.model);
                                                if (model) {
                                                    modelDisplayName = model.name;
                                                }
                                            }

                                            return (
                                                <div key={index} className="rounded-lg border border-gray-200 bg-white p-6 hover:shadow-xl transition-shadow h-full flex flex-col">
                                                    <div className="mb-4 pb-4 border-b">
                                                        <h3 className="text-xl font-semibold text-blue-600">
                                                            {displayData.vendorName || selections.vendor}
                                                        </h3>
                                                        <p className="text-gray-600">{modelDisplayName}</p>
                                                    </div>

                                                    <div className="space-y-2">
                                                        {displayData.compressionClass && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.compression')}:</span>
                                                                <span className="font-semibold">Class {displayData.compressionClass}</span>
                                                            </div>
                                                        )}
                                                        {displayData.lengthType && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.lengthType')}:</span>
                                                                <span className="font-semibold">{displayData.lengthType}</span>
                                                            </div>
                                                        )}
                                                        {displayData.knitting && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.knitting')}:</span>
                                                                <span className="font-semibold">{displayData.knitting}</span>
                                                            </div>
                                                        )}
                                                        {displayData.size && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.size')}:</span>
                                                                <span className="font-semibold">{displayData.size}</span>
                                                            </div>
                                                        )}
                                                        {displayData.lengthsize && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.lengthSize')}:</span>
                                                                <span className="font-semibold">{displayData.lengthsize}</span>
                                                            </div>
                                                        )}
                                                        {displayData.leftright && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.leftright')}:</span>
                                                                <span className="font-semibold">{displayData.leftright}</span>
                                                            </div>
                                                        )}
                                                        {displayData.toeOption && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.toeOption')}:</span>
                                                                <span className="font-semibold">{displayData.toeOption}</span>
                                                            </div>
                                                        )}
                                                        {displayData.bandType && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.bandType')}:</span>
                                                                <span className="font-semibold">{displayData.bandType}</span>
                                                            </div>
                                                        )}
                                                        {displayData.bandWidth && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.bandWidth')}:</span>
                                                                <span className="font-semibold">{displayData.bandWidth}</span>
                                                            </div>
                                                        )}
                                                        {displayData.footLength && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.footLength')}:</span>
                                                                <span className="font-semibold">{displayData.footLength}</span>
                                                            </div>
                                                        )}
                                                        {displayData.ankle && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.ankle')}:</span>
                                                                <span className="font-semibold">{displayData.ankle}</span>
                                                            </div>
                                                        )}
                                                        {displayData.color && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.filters.color')}:</span>
                                                                <span className="font-semibold">{displayData.color}</span>
                                                            </div>
                                                        )}
                                                        {displayData.design && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.design')}:</span>
                                                                <span className="font-semibold">{displayData.design}</span>
                                                            </div>
                                                        )}
                                                        {displayData.pantyShortCompression && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.pantyShortCompression')}:</span>
                                                                <span className="font-semibold">{displayData.pantyShortCompression}</span>
                                                            </div>
                                                        )}
                                                        {displayData.pantyShortSize && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.pantyShortSize')}:</span>
                                                                <span className="font-semibold">{displayData.pantyShortSize}</span>
                                                            </div>
                                                        )}
                                                        {displayData.pantyCrotch && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.pantyCrotch')}:</span>
                                                                <span className="font-semibold">{displayData.pantyCrotch}</span>
                                                            </div>
                                                        )}
                                                        {displayData.fabric && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">{t('measurements.stockingSelection.fabric')}:</span>
                                                                <span className="font-semibold">{displayData.fabric}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="space-y-3 mt-6">
                                                        {shouldShowSaveSelection && (
                                                            <button
                                                                onClick={() => handleSaveProductOnly(product)}
                                                                disabled={isReadOnly || isOrdered}
                                                                className={`w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition-colors font-semibold ${(isReadOnly || isOrdered) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                {t('measurements.stockingSelection.saveSelection')}
                                                            </button>
                                                        )}
                                                        {!isReadOnly && !currentMeasurement?.ordered && (
                                                            <button
                                                                onClick={() => handleProductSelect(product)}
                                                                disabled={!canOrderThisProduct}
                                                                className={`w-full bg-blue-600 text-white py-3 rounded-lg transition-colors font-semibold ${!canOrderThisProduct
                                                                    ? 'opacity-50 cursor-not-allowed'
                                                                    : 'hover:bg-blue-700'
                                                                    }`}
                                                            >
                                                                {t('measurements.stockingSelection.orderThisProduct', { defaultValue: 'Order This Product' })}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="lg:col-span-4 flex flex-col gap-4">
                                    {/* Internal Notes */}
                                    <div className="flex flex-col">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                            {t('patientInfo.fields.notes') || 'Internal Notes'}
                                        </h3>
                                        <textarea
                                            value={internalNotes}
                                            onChange={(e) => setInternalNotes(e.target.value)}
                                            disabled={isReadOnly || isOrdered}
                                            className="w-full min-h-[12rem] border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                            placeholder={t('legMeasuringPage.notesPlaceholder')}
                                        />
                                    </div>
                                    {/* Supplier Order Note */}
                                    <div className="flex flex-col flex-1">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                            {t('measurements.stockingSelection.order.orderNote') || 'Supplier Order Note'}
                                        </h3>
                                        <textarea
                                            value={productNotes}
                                            onChange={(e) => setProductNotes(e.target.value)}
                                            disabled={isReadOnly || isOrdered}
                                            className="w-full flex-1 min-h-[12rem] border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                            placeholder={t('measurements.stockingSelection.order.orderNotePlaceholder') || 'Add notes for this order'}
                                        />
                                    </div>
                                </div>
                            </div>


                        </div>

                        {/* Navigation Buttons - show when in read-only mode or when ordered */}
                        {(isReadOnly || isOrdered) && (
                            <div className="mt-6 pt-6 border-t flex justify-between">
                                <button
                                    type="button"
                                    onClick={handleNavigateToMeasurements}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    {t('measurements.stockingSelection.backMeasurements')}
                                </button>

                                <div className="flex space-x-3">
                                    {/* Next: Go to Order page */}
                                    <button
                                        type="button"
                                        onClick={() => navigate('/order', {
                                            state: {
                                                selectedProduct: displayedMatchingProducts[0],
                                                currentMeasurement: currentMeasurement,
                                                measurementId: location.state?.measurementId,
                                                patientId: location.state?.patientId,
                                                patientName: location.state?.patientName,
                                                measurements: measurements,
                                                compressionClass: compressionClass,
                                                lengthType: lengthType,
                                                knitting: knitting,
                                                selections: selections,
                                                isReadOnly: isReadOnly,
                                                ordered: currentMeasurement?.ordered || isOrdered
                                            }
                                        })}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center font-semibold"
                                    >
                                        {t('measurements.stockingSelection.viewOrder') || 'View Order'}
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate('/patients')}
                                        className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                                    >
                                        {t('patients.buttons.backToPatients')}
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>



            {/* Alert Messages */}
            <Alert type="error" message={error} onDismiss={() => setError('')} />
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />
        </div>
    );
};

export default StockingSelection;
